import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Search, ChevronLeft, ChevronRight, User, MoreVertical, 
  Ban, Trash2, Edit, Eye, Zap, Shield, Phone, Download, FileSpreadsheet
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUsers = () => {
  const { token } = useOutletContext();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [userDetail, setUserDetail] = useState(null);
  const [editForm, setEditForm] = useState({});
  const limit = 20;

  useEffect(() => {
    fetchUsers();
  }, [page, search]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        token,
        skip: page * limit,
        limit
      });
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API}/admin/users?${params}`);
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetail = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}?token=${token}`);
      setUserDetail(response.data);
      setShowDetail(true);
    } catch (error) {
      toast.error('Failed to fetch user details');
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      trust_score: user.trust_score,
      xp: user.xp,
      level: user.level
    });
    setShowEdit(true);
  };

  const handleSaveEdit = async () => {
    try {
      const params = new URLSearchParams({ token, ...editForm });
      await axios.put(`${API}/admin/users/${selectedUser.id}?${params}`);
      toast.success('User updated successfully');
      setShowEdit(false);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleBanUser = async (userId, currentBanStatus) => {
    if (!confirm(currentBanStatus ? 'Unban this user?' : 'Ban this user?')) return;
    try {
      await axios.put(`${API}/admin/users/${userId}?token=${token}&is_banned=${!currentBanStatus}`);
      toast.success(currentBanStatus ? 'User unbanned' : 'User banned');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}?token=${token}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleExport = async (format) => {
    try {
      toast.loading(`Exporting users as ${format.toUpperCase()}...`);
      const response = await axios.get(`${API}/admin/export/users?token=${token}&format=${format}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_export.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.dismiss();
      toast.success(`Users exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to export users');
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name or phone..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="pl-10 bg-slate-800 border-slate-700 text-white rounded-xl"
          />
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('csv')}
            className="border-slate-700 text-slate-300 hover:text-white"
            data-testid="export-users-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('xlsx')}
            className="border-slate-700 text-slate-300 hover:text-white"
            data-testid="export-users-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <span className="text-sm text-slate-400">
            {total} users total
          </span>
        </div>
      </div>

      {/* Users Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-800/50">
              <tr>
                <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">User</th>
                <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Phone</th>
                <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Level</th>
                <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">XP</th>
                <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Trust</th>
                <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Status</th>
                <th className="text-right px-6 py-4 text-slate-400 font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center">
                    <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-slate-400">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-800/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                          <User className="w-5 h-5 text-slate-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{user.name}</p>
                          <p className="text-xs text-slate-500">{user.id.slice(0, 8)}...</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-300">{user.phone}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 rounded-full text-xs font-bold bg-violet-500/20 text-violet-400">
                        Lv{user.level || 1}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-300">{user.xp || 0}</td>
                    <td className="px-6 py-4">
                      <span className={`font-medium ${
                        (user.trust_score || 100) >= 80 ? 'text-green-400' :
                        (user.trust_score || 100) >= 50 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {user.trust_score || 100}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.is_banned ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                      }`}>
                        {user.is_banned ? 'Banned' : 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => fetchUserDetail(user.id)}
                          className="text-slate-400 hover:text-white"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(user)}
                          className="text-slate-400 hover:text-white"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleBanUser(user.id, user.is_banned)}
                          className={user.is_banned ? 'text-green-400 hover:text-green-300' : 'text-yellow-400 hover:text-yellow-300'}
                        >
                          <Ban className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteUser(user.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-slate-800 flex items-center justify-between">
            <span className="text-sm text-slate-400">
              Page {page + 1} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="border-slate-700"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
                className="border-slate-700"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </motion.div>

      {/* User Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          {userDetail && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-slate-700 flex items-center justify-center">
                  <User className="w-8 h-8 text-slate-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">{userDetail.user.name}</h3>
                  <p className="text-slate-400">{userDetail.user.phone}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-800 rounded-xl p-4">
                  <p className="text-slate-400 text-sm">Level</p>
                  <p className="text-2xl font-bold text-violet-400">{userDetail.user.level || 1}</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-4">
                  <p className="text-slate-400 text-sm">XP</p>
                  <p className="text-2xl font-bold text-blue-400">{userDetail.user.xp || 0}</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-4">
                  <p className="text-slate-400 text-sm">Trust Score</p>
                  <p className="text-2xl font-bold text-green-400">{userDetail.user.trust_score || 100}</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-4">
                  <p className="text-slate-400 text-sm">Groups</p>
                  <p className="text-2xl font-bold text-orange-400">{userDetail.groups_count}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-800 rounded-xl p-4">
                  <p className="text-slate-400 text-sm mb-1">Total Contributed</p>
                  <p className="text-xl font-bold text-white">₹{userDetail.total_contributed?.toLocaleString() || 0}</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-4">
                  <p className="text-slate-400 text-sm mb-1">Total Expenses</p>
                  <p className="text-xl font-bold text-white">₹{userDetail.total_expenses?.toLocaleString() || 0}</p>
                </div>
              </div>

              {userDetail.groups && userDetail.groups.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Recent Groups</h4>
                  <div className="space-y-2">
                    {userDetail.groups.slice(0, 5).map((group) => (
                      <div key={group.id} className="bg-slate-800 rounded-xl p-3 flex justify-between">
                        <span>{group.name}</span>
                        <span className="text-slate-400">₹{group.goal_amount?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-400">Name</label>
              <Input
                value={editForm.name || ''}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="mt-1 bg-slate-800 border-slate-700 text-white rounded-xl"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-slate-400">Trust Score</label>
                <Input
                  type="number"
                  value={editForm.trust_score || 0}
                  onChange={(e) => setEditForm({ ...editForm, trust_score: parseInt(e.target.value) })}
                  className="mt-1 bg-slate-800 border-slate-700 text-white rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400">XP</label>
                <Input
                  type="number"
                  value={editForm.xp || 0}
                  onChange={(e) => setEditForm({ ...editForm, xp: parseInt(e.target.value) })}
                  className="mt-1 bg-slate-800 border-slate-700 text-white rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400">Level</label>
                <Input
                  type="number"
                  value={editForm.level || 1}
                  onChange={(e) => setEditForm({ ...editForm, level: parseInt(e.target.value) })}
                  className="mt-1 bg-slate-800 border-slate-700 text-white rounded-xl"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button onClick={() => setShowEdit(false)} variant="outline" className="flex-1 border-slate-700">
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} className="flex-1 bg-gradient-to-r from-red-500 to-orange-500">
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
